import React from 'react';
import { AccountInfoType, UserType } from 'shared/types';

export type RoleType = 'OWNER' | 'ADMIN' | 'ANALYST' | 'VIEWER';

const VIEWER_ACCESS = [
  'survey:read',
  'analytics:read',
  'contact:read',
  'response:read',
  'dashboard:read',
  'queue:read',
] as const;
const ANALYST_ACCESS = [
  'response-impexp:write',
  'contact-impexp:write',
  'workflow:read',
  'response:write',
] as const;
const ADMIN_ACCESS = [
  'settings:read',
  'survey:write',
  'contact:write',
  'response-edit:write',
  'workflow:write',
] as const;
const OWNER_ACCESS = ['account-transfer:write'] as const;

export type PermissionType =
  | (typeof ADMIN_ACCESS)[number]
  | (typeof VIEWER_ACCESS)[number]
  | (typeof ANALYST_ACCESS)[number]
  | (typeof OWNER_ACCESS)[number];

export interface AccessControlParams {
  access: PermissionType[];
  role: RoleType;
  updateRole: (newRole: RoleType) => void;
  user?: UserType;
  account?: AccountInfoType;
  updateUser: (user?: UserType) => void;
  updateAccount: (account?: AccountInfoType) => void;
}

export const ROLE_ACCESS: Record<RoleType, PermissionType[]> = {
  VIEWER: [...VIEWER_ACCESS],
  ANALYST: [...VIEWER_ACCESS, ...ANALYST_ACCESS],
  ADMIN: [...VIEWER_ACCESS, ...ANALYST_ACCESS, ...ADMIN_ACCESS],
  OWNER: [
    ...VIEWER_ACCESS,
    ...ANALYST_ACCESS,
    ...ADMIN_ACCESS,
    ...OWNER_ACCESS,
  ],
};

const defaultParams: AccessControlParams = {
  access: [],
  role: 'VIEWER',
  updateRole: () => {},
  updateAccount: () => {},
  updateUser: () => {},
};

export const AccessControl: React.Context<AccessControlParams> =
  React.createContext<AccessControlParams>(defaultParams);

export const useAccessControl = (): AccessControlParams =>
  React.useContext<AccessControlParams>(AccessControl);
